import React, { useEffect, useState } from "react";
import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Drawer,
} from "@mui/material";
import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  TableContainerWrapper,
} from "../Common/styles/table";
import SearchIcon from "@mui/icons-material/Search";
// import { Edit, MoreVert } from "@mui/icons-material";
import { AxiosResponse } from "axios";
// import { StatusBadge } from "../Common/UI/StatusTag";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
// import AddNewStudyModal from "./AddNewStudyModal";
import { Delete, MoreVert, Settings } from "@mui/icons-material";
import ConfirmDeleteStudyModal from "./ConfirmDeleteStudyModal";
// import EmptyStudyPage from "./EmptyStudyPage";
import AddStudyDetailDrawer from "./AddStudyDetailDrawer";
import SortableHeader from "../Common/UI/SortableHeader";
import { useTranslation } from "react-i18next";

type Props = {
  openDrawer: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: string;
  modifySearchTerm: (val: string) => void;
  canEdit: boolean;
  type: "all-studies" | "my-studies";
};

// type KnownStatusKey = keyof typeof STATUS_MAP;

// const STATUS_MAP = {
//   live: { labelKey: "STATUS_LIVE", variant: "active" },
//   not_live: { labelKey: "STATUS_NOT_LIVE", variant: "cancelled" },
// } as const;

const StudiesList = ({
  searchText,
  modifySearchTerm,
  openDrawer,
  setOpenDrawer,
  canEdit,
  type,
}: Props) => {
  const { t } = useTranslation("studies");
  const [loading, setLoading] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [studyData, setStudyData] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  // const [menuLoader, setMenuLoader] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const open = Boolean(anchorEl);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudyData = async () => {
      try {
        setLoading(true);
        let url = `/study?`;
        if (type === "all-studies") {
          url = `/platform/study?`;
        }
        if (searchText) {
          url += `&query=${searchText}`;
        }
        if (sortOrder && sortColumn) {
          url += `&sortDirection=${sortOrder}&sortKey=${sortColumn}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          studyName: item?.name || "-",
          status: item?.closedOn ? "Locked" : item?.status || "-",
          date: item?.createdAt
            ? DateTime.fromISO(item?.createdAt).toFormat("dd/MM/yy")
            : "-",
        }));

        setStudyData(newData || []);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchStudyData();
  }, [searchText, toggleLoader, sortOrder, sortColumn, type]);

  const deleteStudy = async () => {
    setOpenDeleteModal(true);
    setAnchorEl(null);
  };
  const closeDeleteModal = async () => {
    setOpenDeleteModal(false);
    handleMenuClose();
  };
  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleCellClick = (id: string) => {
    if (type === "all-studies") return;
    navigate(`/studies/${id}`);
  };
  // const openModal = () => {
  //   setShowModal(true);
  //   setAnchorEl(null);
  // };

  // const closeModal = () => {
  //   setShowModal(false);
  //   setSelectedRow(null);
  // };
  const closeDrawer = () => {
    setOpenDrawer(false);
    setSelectedRow(null);
    refreshPage();
  };
  const handleSort = (column: string) => {
    if (column === sortColumn) {
      if (sortOrder === "ASC") {
        setSortOrder("DESC");
      } else {
        setSortOrder("");
        setSortColumn("");
      }
    } else {
      setSortColumn(column);
      setSortOrder("ASC");
    }
  };
  const handleSettings = (id: string, studyName: string) => {
    navigate(`/app/all-studies/study/${id}?study=${studyName}`);
  };

  return (
    <TableContainerWrapper>
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder={t("SEARCH_PLACEHOLDER")}
          defaultValue={searchText}
          onChange={(e) => modifySearchTerm(e.target.value)}
          size="small"
          sx={{ width: "70%", minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table sx={TableBorderRadiusTopLeftRight}>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <SortableHeader
                  label={t("TABLE_STUDY_NAME")}
                  column="name"
                  sortColumn={sortColumn}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
              </StyledTableCell>
              {/* <StyledTableCell>
                <Box display={"flex"} alignItems={"center"}>
                  {t("TABLE_STATUS")}
                </Box>
              </StyledTableCell> */}
              <StyledTableCell>
                <SortableHeader
                  label={t("TABLE_CREATION_DATE")}
                  column="createdAt"
                  sortColumn={sortColumn}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
              </StyledTableCell>
              {canEdit && (
                <StyledTableCell align="right">{t("TABLE_ACTIONS")}</StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          {!loading && studyData?.length > 0 && (
            <TableBody>
              {studyData?.map((row: any) => (
                <TableRow key={row?.id}>
                  <StyledTableCell
                    onClick={() => handleCellClick(row?.id)}
                    sx={{ cursor: type === "all-studies" ? "default" : "pointer" }}
                  >
                    {row?.studyName}
                  </StyledTableCell>
                  {/* <StyledTableCell>
                    <StatusBadge
                      variant={
                        STATUS_MAP[row?.status?.toLowerCase() as KnownStatusKey]?.variant ||
                        "active"
                      }
                      label={t(STATUS_MAP[row?.status?.toLowerCase() as KnownStatusKey]?.labelKey || "STATUS_LIVE")}
                    />
                  </StyledTableCell> */}
                  <StyledTableCell>{row?.date}</StyledTableCell>

                  {row?.status === "not_live" && canEdit && (
                    <StyledTableCell align="right">
                      <Box>
                        {type === "all-studies" && (
                          <IconButton
                            onClick={() =>
                              handleSettings(row?.id, row?.studyName)
                            }
                          >
                            <Settings />
                          </IconButton>
                        )}
                        <IconButton onClick={(e) => handleMenuClick(e, row)}>
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </StyledTableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </Box>
      {!loading && studyData?.length === 0 && (
        <NoDataContainer>
          <Typography variant="body1" color="gray">
            {t("NO_DATA", { ns: "common" })}
          </Typography>
        </NoDataContainer>
      )}
      {loading && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: "400px",
            width: "20ch",
          },
        }}
      >
        <MenuItem onClick={deleteStudy}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("MENU_DELETE")}</ListItemText>
          {/* {menuLoader && (
            <ListItemIcon>
              <CircularProgress size={18} sx={{ ml: 1 }} />
            </ListItemIcon>
          )} */}
        </MenuItem>
      </Menu>

      {/* {showModal && (
        <AddNewStudyModal
          showModal={showModal}
          closeModal={closeModal}
          data={selectedRow}
          refreshPage={refreshPage}
        />
      )} */}
      {openDeleteModal && (
        <ConfirmDeleteStudyModal
          openModal={openDeleteModal}
          selectedRow={selectedRow}
          closeModal={closeDeleteModal}
          refreshPage={refreshPage}
        />
      )}
      <Drawer open={openDrawer} onClose={closeDrawer} anchor={"right"}>
        <AddStudyDetailDrawer
          closeDrawer={closeDrawer}
          refreshPage={refreshPage}
        />
      </Drawer>
    </TableContainerWrapper>
  );
};

export default StudiesList;
